import { type FC } from "react";
import { useDispatch } from "react-redux";

import { Card, CardRowSimple, Copyable, type GridCard, Tag, TagArrow, TagIntent } from "widgets";

import style from "../../core/cards/styles/Card.module.scss";

import { type Marker } from "../../../graphql.js";
import { type ReduxDispatch } from "../../../redux/redux-utils.js";
import { COLOR } from "../../../settings/colors-settings.js";

export interface IMarkerCardProps extends GridCard {
  marker: Marker;
}

const MarkerCard: FC<IMarkerCardProps> = ({ marker }) => {
  const dispatch = useDispatch<ReduxDispatch>();
  if (!marker) {
    return null;
  }

  const sequenceBuilder = () => {
    const SEQUENCE_REGEX = /([A-Z]*)((\[)([A-Z]+)(\/)([A-Z]+)(]))([A-Z.]*)/g;
    const threshold = 400;

    // get marker sequence if it exists
    let nuFeatureSequence = marker.sequenceContent?.toUpperCase() ?? "";

    // truncate
    nuFeatureSequence?.length >= threshold
      ? (nuFeatureSequence = nuFeatureSequence.slice(0, threshold) + "...")
      : nuFeatureSequence;

    const array = [...nuFeatureSequence?.matchAll(SEQUENCE_REGEX)];

    if (array.length === 0) {
      return nuFeatureSequence;
    }

    return array.map((part, index) => {
      return (
        <div style={{ wordBreak: "break-word" }} key={`marker-sequence-${index}`}>
          {part[1]}
          <span style={{ color: COLOR.LIPSTICK.NORMAL, fontWeight: 800 }}>{part[3]}</span>
          <span style={{ color: COLOR.LIPSTICK.NORMAL }}>{part[4]}</span>
          <span style={{ color: COLOR.LIPSTICK.NORMAL, fontWeight: 800 }}>{part[5]}</span>
          <span style={{ color: COLOR.LIPSTICK.NORMAL }}>{part[6]}</span>
          <span style={{ color: COLOR.LIPSTICK.NORMAL, fontWeight: 800 }}>{part[7]}</span>
          {part[8]}
        </div>
      );
    });
  };

  return (
    <Card key="marker-info-card" title={"Marker Info"}>
      <CardRowSimple label={"Type"}>
        {marker.markerType.name}
        <div className={style.description}>{marker.markerType?.description}</div>
      </CardRowSimple>
      <CardRowSimple label={"Name"}>{marker.name}</CardRowSimple>
      {marker.synonyms?.length > 0 && (
        <CardRowSimple label={"Synonyms"}>
          <div className={style.tags_list}>
            {marker.synonyms.map((syn, index) => (
              <Tag label={syn} intentColor={TagIntent.Secondary} key={`marker-synonyms-${index}`} />
            ))}
          </div>
        </CardRowSimple>
      )}
      <CardRowSimple label={"Description"}>{marker.description}</CardRowSimple>
      <CardRowSimple label={"Species"}>{marker.species.name}</CardRowSimple>
      {marker.species.lineage && (
        <CardRowSimple label={"Lineage"}>
          <div className={style.tags_list}>
            {marker.species.lineage.split(";").map((lin, index) => (
              <TagArrow key={`marker-lineage-${index}`} label={lin.trim()} />
            ))}
          </div>
        </CardRowSimple>
      )}
      {marker.sequenceContent && (
        <CardRowSimple label="Sequence">
          <div>
            <Copyable getTextContent={async () => marker.sequenceContent ?? ""}>
              <span style={{ fontFamily: "Roboto Flex Mono, monospace", fontSize: 13 }}>{sequenceBuilder()}</span>
            </Copyable>
          </div>
        </CardRowSimple>
      )}
    </Card>
  );
};

export { MarkerCard };
